import memoize from 'lodash/memoize';
import { createSelector } from 'reselect';

const fallbackArray = [];

export const getMeUser = ({ auth: { user } }) => user;
export const getWorkspaceColor = ({ auth: { workspace } }) => workspace?.color;

export const getIntercom = ({ auth: { intercom } }) => intercom;

export const getWorkspace = ({ auth: { workspace } }) => workspace;
export const getWorkspaceId = ({ auth: { workspace } }) => workspace?.id;
export const getWorkspaceLocale = ({ auth: { workspace } }) => workspace?.locale;

export const getMemberId = ({ auth: { workspace } }) => workspace?.memberId;

export const isActiveWorkspaceAdmin = createSelector(getWorkspace, workspace => ['OWNER', 'ADMIN'].includes(workspace?.role));
export const isActiveWorkspaceContentCreator = createSelector(getWorkspace, workspace => ['CONTENT_CREATOR'].includes(workspace?.role));

const isMemberOf = (member, workspace) => !!member && member.workspaceId === workspace?.id && member.memberId === workspace?.memberId;

// A member loaded for another workspace or membership counts as none, so nothing borrows another session's permissions
export const getMember = ({ auth }) => (isMemberOf(auth?.member, auth?.workspace) ? auth.member : null);
export const getIntegrations = state => getMember(state)?.integrations;

// From the member rather than auth/user, so the status always comes with the permissions it grants
export const getMemberStatus = state => getMember(state)?.memberStatus;
export const isPendingMember = state => getMemberStatus(state) === 'PENDING';

const getWorkspacePermissions = state => getMember(state)?.permissions || fallbackArray;

export const getUserIsLoggedIn = ({ auth: { isLoggedIn } }) => isLoggedIn;

const getNotificationCount = ({ auth: { user: { notificationCount } } }) => notificationCount;
const getLocale = ({ auth: { user: { locale } } }) => locale;

const getFeatures = state => getMember(state)?.features || fallbackArray;

// Changes only when what the member may see changes, not on every reload of the same member
const getMemberKey = createSelector([getMember], member => (member ? JSON.stringify([member.permissions, member.features]) : null));

const getFeatureSelector = createSelector(
  [getFeatures],
  (features) => memoize(feature => features?.[feature]),
);

const hasPermissionSelector = createSelector(
  [getWorkspacePermissions],
  (permissions) => memoize(permission => (permissions || []).includes(permission)),
);

const hasAnyPermissionSelector = createSelector(
  [hasPermissionSelector],
  (hasPermission) => memoize((...requiredPermissions) => !!requiredPermissions.some(required => hasPermission(required))),
);

const hasFeatureSelector = createSelector(
  [getFeatureSelector],
  (getFeature) => memoize(requiredFeature => !!getFeature(requiredFeature)?.enabled),
);

const hasAnyFeatureSelector = createSelector(
  [hasFeatureSelector],
  (hasFeature) => memoize((...requiredFeatures) => !!requiredFeatures.some(required => hasFeature(required))),
);

export default {
  getMeUser,
  getIntercom,
  getWorkspaceColor,
  getWorkspace,
  getWorkspaceId,
  getWorkspaceLocale,
  getWorkspacePermissions,
  getMember,
  getMemberKey,
  getIntegrations,

  getFeatureSelector,
  hasPermissionSelector,
  hasAnyPermissionSelector,
  hasFeatureSelector,
  hasAnyFeatureSelector,

  getMemberId,
  getMemberStatus,
  isPendingMember,
  isActiveWorkspaceAdmin,
  isActiveWorkspaceContentCreator,
  getUserIsLoggedIn,
  getLocale,

  getNotificationCount,
};
